import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Container,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Grid,
  InputLabel,
  Paper,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material'
import { useContext } from 'react'
import { HotelContext } from '../contexts/HotelContext'
import { BackButton } from '../components/BackButton'
import { useLocation, useNavigate, useParams } from 'react-router'
import qs from 'qs'
import { useQuery } from '@tanstack/react-query'
import { createBooking, searchAvailability } from '../apis/hotelApis'
import { PriceOptions } from '../types/bookingFormTypes'
import { RoomSearchResult } from '../modules/RoomSearchResult'
import { DateTime } from 'luxon'
import { DateTimeFormats, PHONE_REGEX, PaymentMethod, language } from '../common/constants'
import { formatDuration } from '../utils/stringUtils'
import { formatCurrency } from '../utils/numberUtil'
import { Loader, OverlayLoader } from '../components/Loader'
import { Controller, useForm } from 'react-hook-form'
import { useSnackbar } from 'notistack'

interface ISearchParams {
  from: string
  to: string
  priceOption: PriceOptions
  hotelId: string
  roomId: string
}

interface IFormState {
  phoneNumber: string
  contactName: string
  paymentMethod: PaymentMethod
}

export const ReviewPage = () => {
  const { hotel } = useContext(HotelContext)
  const routeParams = useParams()
  const { search } = useLocation()
  const navigate = useNavigate()
  const searchParams = qs.parse(search, {
    ignoreQueryPrefix: true,
  })
  const { enqueueSnackbar } = useSnackbar()
  const { from, to, priceOption, hotelId, roomId } = { ...routeParams, ...searchParams } as any as ISearchParams
  const { data, isLoading, error } = useQuery({
    queryKey: ['searchAvailability', hotel.hotelId, priceOption, from, to],
    queryFn: () =>
      searchAvailability(hotelId, {
        from,
        to,
        priceOption,
      }),
  })
  const hours = DateTime.fromISO(to).diff(DateTime.fromISO(from), 'hours')?.hours
  const nights = Math.ceil(Math.max(DateTime.fromISO(to).diff(DateTime.fromISO(from), 'days')?.days, 1))

  const roomInfo = hotel.rooms.find((item) => item.roomTypeId.toString() === roomId)
  const roomAvailability = data?.find((item) => item.RoomTypeId.toString() === roomId)
  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<IFormState>({
    defaultValues: {
      phoneNumber: '',
      contactName: '',
      paymentMethod: PaymentMethod.VietQR_VietinBank,
    },
    mode: 'all',
  })

  const onSubmit = async (values: IFormState) => {
    try {
      const result = await createBooking(hotel.hotelId, {
        contactName: values.contactName,
        from: from,
        to: to,
        phoneNumber: values.phoneNumber,
        paymentMethod: values.paymentMethod,
        priceOption: priceOption,
        roomPriceId: roomAvailability?.RoomPriceId,
        roomTypeId: roomAvailability.RoomTypeId,
        emailInfo: {
          priceInfo: roomAvailability,
        },
      })
      navigate(`/hotel/${hotel.hotelId}/reservation/${result.Id}`, {
        state: {
          phoneNumber: values.phoneNumber,
        },
      })
    } catch (e) {
      enqueueSnackbar(language.GENERIC_ERROR, {
        variant: 'error',
      })
    }
  }

  if (isLoading) {
    return <OverlayLoader />
  }

  return (
    <Container sx={{ flex: 1 }} maxWidth="md">
      <BackButton to={`/hotel/${hotel.hotelId}/search`} />
      <Box padding={1}>
        <Typography
          color={(theme) => theme.palette.primary.main}
          mt={1}
          textAlign="center"
          variant="h4"
          gutterBottom
          fontWeight="600">
          Nhập thông tin đặt phòng
          <Typography color={(theme) => theme.palette.grey[600]} textAlign="center">
            Thông tin này sẽ được dùng để bạn quản lý đặt phòng
          </Typography>
        </Typography>
      </Box>
      {isSubmitting && <OverlayLoader />}
      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} lg={4}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Typography fontWeight={500} textAlign="center" gutterBottom>
                  Chi tiết đặt phòng
                </Typography>
                <Box>
                  <Typography fontSize={'1rem'} gutterBottom>
                    <span>Nhận phòng: </span>
                    <b>{DateTime.fromISO(from).toFormat(DateTimeFormats.FULL)}</b>
                  </Typography>
                </Box>
                <Box>
                  <Typography fontSize={'1rem'} gutterBottom>
                    <span>Trả phòng: </span>
                    <b>{DateTime.fromISO(to).toFormat(DateTimeFormats.FULL)}</b>
                  </Typography>
                </Box>
                <Box>
                  <Typography fontSize={'1rem'} gutterBottom>
                    <span>Tổng thời gian lưu trú: </span>
                    <b>{formatDuration({ priceOption, hours, nights })}</b>
                  </Typography>
                </Box>
                <Box>
                  <Typography fontSize={'1rem'} gutterBottom>
                    <span>Khách sạn: </span>
                    <b>{hotel.hotelName}</b>
                  </Typography>
                </Box>
                <Box>
                  <Typography fontSize={'1rem'}>
                    <span>{roomInfo.roomTypeName}: </span>
                    <b>{formatCurrency(roomAvailability.TotalPrice)}</b>
                  </Typography>
                  <FormHelperText>(Giá đã bao gồm thuế và phụ phí)</FormHelperText>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} lg={4}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Typography fontWeight={500} textAlign="center">
                  Thông tin đặt phòng
                </Typography>
                <FormControl margin="normal" fullWidth>
                  <Controller
                    name="phoneNumber"
                    rules={{
                      pattern: PHONE_REGEX,
                      required: true,
                    }}
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        error={Boolean(errors?.phoneNumber)}
                        size="small"
                        autoComplete="off"
                        inputProps={{
                          'aria-autocomplete': 'none',
                        }}
                        type="number"
                        label="Số điện thoại"
                        required
                      />
                    )}
                  />
                  <FormHelperText>Vui lòng cung cấp sđt gọi được để xác thực đặt phòng.</FormHelperText>
                </FormControl>
                <FormControl margin="normal" fullWidth>
                  <Controller
                    name="contactName"
                    control={control}
                    rules={{
                      required: true,
                    }}
                    render={({ field }) => (
                      <TextField
                        error={Boolean(errors?.contactName)}
                        {...field}
                        size="small"
                        autoComplete="off"
                        inputProps={{
                          'aria-autocomplete': 'none',
                        }}
                        label="Tên liên hệ"
                        required
                      />
                    )}
                  />
                </FormControl>
                <FormControl margin="normal" fullWidth>
                  <FormLabel sx={{ fontSize: '1rem' }} id="demo-radio-buttons-group-label">
                    Phương thức thanh toán
                  </FormLabel>
                  <Controller
                    name="paymentMethod"
                    control={control}
                    render={({ field }) => (
                      <RadioGroup
                        {...field}
                        aria-labelledby="demo-radio-buttons-group-label"
                        defaultValue="qr"
                        name="radio-buttons-group">
                        <FormControlLabel
                          sx={{
                            fontSize: '1rem',
                          }}
                          value={PaymentMethod.VietQR_VietinBank}
                          control={<Radio size="small" />}
                          label={<Typography fontSize="1rem">Quét mã QR trên ứng dụng ngân hàng</Typography>}
                        />
                      </RadioGroup>
                    )}
                  />
                </FormControl>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
        <Box mt={3}>
          <Button size="large" disabled={isSubmitting} fullWidth variant="contained" type="submit">
            Nhấn để xác nhận đặt phòng
          </Button>
        </Box>
      </form>
    </Container>
  )
}
